/**
 * PwaUpdatePrompt — «Доступно обновление» баннер для PWA.
 *
 * Приложение — client-only PWA с service worker. При client-side роутинге
 * (react-router) пользователь почти никогда не делает полный reload, поэтому
 * новый SW «зависает» в waiting и обновление не доезжает — пользователь остаётся
 * на устаревшей версии. Этот баннер (режим `prompt` в vite.config) показывает,
 * что новая версия готова, и по клику активирует её + перезагружает страницу.
 */
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Пока вкладка открыта — проверять наличие новой версии раз в час.
// Так «долгоживущие» вкладки не остаются на устаревшей версии между релизами.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

export const PwaUpdatePrompt: FC = () => {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => {
          void registration.update();
        }, UPDATE_CHECK_INTERVAL_MS);
      }
    },
  });

  return (
    <AnimatePresence>
      {needRefresh && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          role="status"
          aria-live="polite"
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:max-w-sm z-[70] bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-xl shadow-slate-900/10 dark:shadow-none p-4 flex items-start gap-3"
        >
          <div className="w-9 h-9 shrink-0 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <RefreshCw size={18} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm text-slate-800 dark:text-slate-100">
              {t('pwa.update_title', 'Доступно обновление')}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {t('pwa.update_desc', 'Готова новая версия приложения.')}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => void updateServiceWorker(true)}
                className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors"
              >
                {t('pwa.update_action', 'Обновить')}
              </button>
              <button
                type="button"
                onClick={() => setNeedRefresh(false)}
                className="px-3 py-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold transition-colors"
              >
                {t('pwa.update_later', 'Позже')}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setNeedRefresh(false)}
            className="shrink-0 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            aria-label={t('common.close', 'Закрыть')}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PwaUpdatePrompt;
